/**
 * The words on the job the customer reads: what was done, and their order
 * number. The invoice and the docket print both — "Scope of works completed",
 * "Services performed" — so the place to write them is on the job, while the
 * tech still remembers (DOC-1).
 *
 * Saves through `actions.writeUp`, which queues when there is no signal.
 */
import { useState } from "react";

import * as actions from "../lib/actions";
import { ApiError, openDocument } from "../lib/api";
import type { JobOut } from "../lib/types";
import { Card } from "./ui";

export function JobWriteUp({ job, onSaved }: { job: JobOut; onSaved: () => void }) {
  const [workDone, setWorkDone] = useState(job.work_done ?? "");
  const [po, setPo] = useState(job.purchase_order_number ?? "");
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const dirty = workDone !== (job.work_done ?? "") || po !== (job.purchase_order_number ?? "");

  async function save() {
    setBusy(true);
    try {
      const { queued, message } = await actions.writeUp(job.id, {
        work_done: workDone,
        purchase_order_number: po,
      });
      setSaid({ tone: "info", text: queued ? (message ?? "Saved on this phone.") : "Saved." });
      onSaved();
    } catch (err) {
      setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't save it." });
    } finally {
      setBusy(false);
    }
  }

  async function docket() {
    try {
      await openDocument(`/jobs/${job.id}/docket`);
    } catch (err) {
      setSaid({ tone: "crit", text: err instanceof ApiError && err.status === 404
        ? "No work order template yet — an office user can upload one in Settings → Documents."
        : err instanceof ApiError ? err.message : "Couldn't open the docket." });
    }
  }

  return (
    <Card title="Write-up" subtitle="Printed on the invoice and the docket">
      <label className="field">
        <span>Work done</span>
        <textarea
          rows={4}
          value={workDone}
          onChange={(e) => setWorkDone(e.target.value)}
          placeholder="Describe the work completed, materials used, checks performed and any follow-up needed."
        />
      </label>
      <label className="field" style={{ marginTop: 8 }}>
        <span>Customer's order number</span>
        <input value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO-77812" />
      </label>
      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"} style={{ marginTop: 8 }}>
          {said.text}
        </p>
      )}
      <div className="btn-row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" type="button" disabled={!dirty || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="btn" type="button" onClick={() => void docket()}>
          Print docket
        </button>
      </div>
    </Card>
  );
}
